// packages/frontend/src/components/AuthContext.tsx
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import axios from 'axios';
import { getAuth, onIdTokenChanged, signOut } from 'firebase/auth';
import { doc, getDoc, getFirestore } from 'firebase/firestore';

interface AuthContextProps {
  currentUser: User;
  idToken: string;
  isInitializing: boolean;
  isAuthenticated: boolean;
  isFetchingPipedriveData: boolean;
  authError: string | null;
  clearAuthError: () => void;
}

type User = null | {
  [key: string]: any; // Utilisez `any` ou `Record<string, any>` pour un type flexible
};

export const AuthContext = createContext<AuthContextProps | null>(null);

// Récupérer les données de la personne via le backend (sécurisé, pas d'exposition de clé API)
const fetchPersonDataFromBackend = async (email: string) => {
  try {
    const response = await axios.get(`/api/pipedrive/persons-with-org-by-email/${encodeURIComponent(email)}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching person data from backend:', error);
    return null;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User>(null);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isFetchingPipedriveData, setIsFetchingPipedriveData] = useState<boolean>(true);
  const [idToken, setIdToken] = useState<string>('');
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthError = () => setAuthError(null);

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setIsInitializing(true); // Indique que l'initialisation est en cours

      if (user) {
        setIsFetchingPipedriveData(true); // Indique que la récupération des données de Pipedrive commence

        try {
          const docSnap = await getDoc(doc(db, 'Users', user.uid));
          if (docSnap.exists()) {
            const userData = { ...user, data: docSnap.data(), authUser: user };
            const userEmail = userData.email;

            // Récupérer les données de la personne via le backend (sécurisé)
            const personData = await fetchPersonDataFromBackend(userEmail);

            if (personData && personData.organization_id) {
              // Transformer les données du backend pour correspondre au format attendu
              const formattedPersonData = {
                id: personData.id,
                name: personData.name,
                first_name: personData.first_name,
                last_name: personData.last_name,
                email: personData.email,
                phone: personData.phone,
                organization: {
                  name: personData.organization_name,
                  value: personData.organization_id,
                },
                org_id: personData.organization_id,
                // Email de l'organisation depuis la BDD (priorité pour les factures)
                organization_email: personData.organization_email,
                // Inclure les autres champs utiles
                ...personData,
              };
              const mergedData = { ...userData, ...formattedPersonData };
              setCurrentUser({ ...user, data: mergedData, authUser: user });
            } else {
              console.error(`No person with organization found for user ${userEmail}`);
              setAuthError(
                'Votre compte n\'est pas rattaché à une organisation. Veuillez contacter le support à luc@aptekaa.fr pour résoudre ce problème.',
              );
              await signOut(auth);
              setCurrentUser(null);
              setIsFetchingPipedriveData(false);
              setIsInitializing(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
        }

        setIsFetchingPipedriveData(false); // Indique que la récupération des données de Pipedrive est terminée

        // Récupérer le token d'ID et le mettre à jour dans l'état
        user
          .getIdToken()
          .then((token: string) => {
            setIdToken(token);
          })
          .catch((error: any) => {
            console.error('Error getting ID token:', error);
          });
      } else {
        setCurrentUser(null); // Réinitialiser l'utilisateur actuel
        setIdToken(''); // Réinitialiser le token d'ID
        setIsFetchingPipedriveData(false); // Indique que la récupération des données de Pipedrive est terminée
      }

      setIsInitializing(false); // Indique que l'initialisation est terminée
    });

    return () => unsubscribe(); // Nettoyer l'abonnement lors du démontage
  }, [auth, db]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        idToken,
        isInitializing,
        isAuthenticated: currentUser !== null,
        isFetchingPipedriveData,
        authError,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextProps {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context as AuthContextProps; // assert non-null
}
